import { useState, useRef, useMemo } from 'react';
import { Search, Upload, CheckCircle, History, User, Phone, Home, ChevronRight, Thermometer, Lock, ClipboardList } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useOffline } from '../../contexts/OfflineContext';
import { uploadLockPhoto } from '../../utils/storage';
import { isActiveWO, findUnitForWO } from '../../utils/dataQueries';
import { HEAT_TYPES, AC_TYPES, FIELD_LABELS, formatLockCodes, coerceLockCounts } from '../../utils/unitDetailsHelpers';
import { colors, Header, Card, SelectField, Button, PillToggle, LockFields } from '../shared';

const LocksScreen = () => {
  const {
    getOpenWOsForTech,
    units,
    workOrders,
    getCurrentTech,
    getWOsForUnit,
    getUnitDetails,
    unitDetails,
    unitDetailChanges,
    saveUnitDetails,
  } = useData();
  const { isOnline, queuePhoto, canQueuePhoto } = useOffline();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPreConfirm, setShowPreConfirm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [changeNotes, setChangeNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastSavedChanges, setLastSavedChanges] = useState([]);
  const [viewMode, setViewMode] = useState('open');

  const fileInputRef = useRef(null);
  const currentTech = getCurrentTech();

  // Helper: build HVAC summary string for list cards
  const hvacSummary = (details) => {
    if (!details) return null;
    const parts = [details.heatType, details.filterSize].filter(Boolean);
    return parts.length > 0 ? parts.join(' \u00B7 ') : null;
  };

  // Get open WOs for current tech with unit details
  const openWOs = getOpenWOsForTech();
  const openWOUnits = useMemo(() => {
    return openWOs.map(wo => {
      const unit = findUnitForWO(units, wo);
      const details = getUnitDetails(wo.unitId);

      return {
        id: wo.unitId || wo.unit,
        unitId: wo.unitId,
        propertyId: wo.propertyId,
        name: unit?.name || wo.unit || wo.property,
        property: wo.property,
        address: unit?.address || unit?.name || wo.property,
        details,
        woId: wo.id,
        woTask: wo.description,
        woStatus: wo.status,
        resident: wo.resident,
        residentPhone: wo.residentPhone,
        bedrooms: unit?.bedrooms,
        bathrooms: unit?.bathrooms,
        sqft: unit?.sqft,
      };
    }).filter(u => u.id);
  }, [openWOs, units, getUnitDetails]);

  // All units — driven by AppFolio units collection (source of truth for what units exist)
  // Filtered to active visibility only, enriched with unit_details when available
  const allUnits = useMemo(() => {
    const activeUnits = units.filter(u =>
      !u.visibility || u.visibility.toLowerCase() === 'active'
    );

    return activeUnits.map(u => {
      const details = getUnitDetails(u.id);
      const unitWOs = getWOsForUnit(u.id);
      const openWO = unitWOs.find(wo => isActiveWO(wo));
      const latestWO = unitWOs[0];

      return {
        id: u.id,
        unitId: u.id,
        propertyId: u.propertyId,
        name: u.name || u.address,
        property: u.propertyName || u.property,
        address: u.address || u.name,
        details,
        woId: openWO?.id || latestWO?.id,
        woTask: openWO?.description || latestWO?.description,
        woStatus: openWO?.status || latestWO?.status,
        resident: latestWO?.resident,
        residentPhone: latestWO?.residentPhone,
        hasOpenWO: !!openWO,
        bedrooms: u.bedrooms,
        bathrooms: u.bathrooms,
        sqft: u.sqft,
      };
    });
  }, [units, getUnitDetails, getWOsForUnit]);

  // Dropdown options
  const heatOptions = HEAT_TYPES.map(h => ({ value: h, label: h }));
  const acOptions = AC_TYPES.map(a => ({ value: a, label: a }));

  // Filter based on search and view mode
  const displayUnits = viewMode === 'open' ? openWOUnits : allUnits;

  const filtered = search
    ? displayUnits.filter(u => {
        const s = search.toLowerCase();
        return (
          String(u.id || '').toLowerCase().includes(s) ||
          (u.name || '').toLowerCase().includes(s) ||
          (u.property || '').toLowerCase().includes(s) ||
          (u.address || '').toLowerCase().includes(s) ||
          String(u.woId || '').toLowerCase().includes(s)
        );
      })
    : displayUnits;

  // ── Form helpers ─────────────────────────────────────────────

  const initForm = (details) => {
    setFormValues({
      heatType: details?.heatType || '',
      acType: details?.acType || '',
      filterSize: details?.filterSize || '',
      hvacNotes: details?.hvacNotes || '',
      unitLockCode: details?.unitLockCode || '',
      unitLockType: details?.unitLockType || '',
      unitLockCount: details?.unitLockCount ?? '',
      unitLockNotes: details?.unitLockNotes || '',
      tenantLockbox: details?.tenantLockbox || '',
      commonLockCode: details?.commonLockCode || '',
      commonLockType: details?.commonLockType || '',
      commonLockCount: details?.commonLockCount ?? '',
      commonLockNotes: details?.commonLockNotes || '',
    });
    setChangeNotes('');
    setPhotoFile(null);
  };

  const updateField = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  // Compute diff for pre-confirm
  const pendingChanges = useMemo(() => {
    if (!selected) return [];
    const current = selected.details || {};
    const changes = [];
    for (const field of Object.keys(FIELD_LABELS)) {
      const normalize = (v) => (v === undefined || v === null || v === '') ? null : String(v);
      const from = normalize(current[field]);
      const to = normalize(formValues[field]);
      if (from !== to) {
        changes.push({ field, label: FIELD_LABELS[field], from: from || '(empty)', to: to || '(empty)' });
      }
    }
    return changes;
  }, [selected, formValues]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setPhotoFile(file);
  };

  const handleSave = async () => {
    setUploading(true);
    try {
      let photoUrl = null;
      let photoPath = null;

      if (photoFile) {
        if (isOnline) {
          const result = await uploadLockPhoto(photoFile, selected.unitId);
          photoUrl = result.url;
          photoPath = result.path;
        } else if (canQueuePhoto) {
          const queued = queuePhoto({ type: 'lock', unitId: selected.unitId, file: photoFile });
          if (!queued.success) {
            alert(queued.error);
            setUploading(false);
            return;
          }
        }
      }

      // Build values to save (only trackable fields + optional photo)
      const valuesToSave = coerceLockCounts(formValues);

      if (photoUrl) valuesToSave.photoUrl = photoUrl;
      if (photoPath) valuesToSave.photoPath = photoPath;
      if (changeNotes) valuesToSave.changeNotes = changeNotes;

      await saveUnitDetails(selected.unitId, valuesToSave);

      setLastSavedChanges(pendingChanges);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error saving unit details:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setShowPreConfirm(false);
    setShowEditForm(false);
    // Refresh selected unit's details from state
    const freshDetails = getUnitDetails(selected.unitId);
    setSelected(prev => prev ? { ...prev, details: freshDetails } : null);
    setFormValues({});
    setChangeNotes('');
    setPhotoFile(null);
    setLastSavedChanges([]);
  };

  // ── Pre-confirm modal ────────────────────────────────────────

  const PreConfirmModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <Card className="w-full max-w-sm max-h-[80vh] overflow-y-auto">
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: colors.yellowDim }}>
            <ClipboardList size={36} style={{ color: colors.yellow }} />
          </div>
          <h3 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>Confirm Changes</h3>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            {pendingChanges.length} field{pendingChanges.length !== 1 ? 's' : ''} will be updated:
          </p>
        </div>

        <div className="rounded-xl p-3 mb-4 space-y-3" style={{ background: colors.inputBg }}>
          {pendingChanges.map((c, i) => (
            <div key={c.field} className={`${i > 0 ? 'pt-3 border-t' : ''}`} style={{ borderColor: colors.border }}>
              <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>{c.label}</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-mono" style={{ color: colors.textMuted }}>{c.from}</span>
                <span style={{ color: colors.textMuted }}>&rarr;</span>
                <span className="font-mono font-bold" style={{ color: colors.yellow }}>{c.to}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => setShowPreConfirm(false)} className="flex-1">
            Go Back
          </Button>
          <button
            onClick={() => { setShowPreConfirm(false); handleSave(); }}
            disabled={uploading}
            className="flex-1 py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: colors.yellow, color: colors.black }}
          >
            {uploading ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </Card>
    </div>
  );

  // ── Success modal ────────────────────────────────────────────

  const ConfirmationModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <Card className="w-full max-w-sm">
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: colors.greenDim }}>
            <CheckCircle size={36} style={{ color: colors.green }} />
          </div>
          <h3 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>Unit Details Updated!</h3>
          <div className="text-sm space-y-1" style={{ color: colors.textSecondary }}>
            {lastSavedChanges.map(c => (
              <p key={c.field}>
                {c.label}: <span className="font-mono font-bold" style={{ color: colors.yellow }}>{c.to}</span>
              </p>
            ))}
          </div>
        </div>
        <button
          onClick={handleConfirmationClose}
          className="w-full py-3 px-4 rounded-xl font-bold"
          style={{ background: colors.yellow, color: colors.black }}
        >
          Done
        </button>
      </Card>
    </div>
  );

  // ── Edit Form View ────────────────────────────────────────────

  if (showEditForm && selected) {
    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        {showPreConfirm && <PreConfirmModal />}
        {showConfirmation && <ConfirmationModal />}
        <Header
          title="Edit Unit Details"
          subtitle={selected.name || selected.address || `Unit ${selected.id}`}
          showBack
          onBack={() => setShowEditForm(false)}
        />
        <div className="px-4">
          {/* HVAC Section */}
          <Card className="mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Thermometer size={18} style={{ color: colors.yellow }} />
              <span className="font-semibold" style={{ color: colors.yellow }}>HVAC & Filters</span>
            </div>
            <SelectField
              label="Heat Type"
              value={formValues.heatType}
              onChange={(v) => updateField('heatType', v)}
              options={heatOptions}
              placeholder="-- Select heat type --"
            />
            <SelectField
              label="AC Type"
              value={formValues.acType}
              onChange={(v) => updateField('acType', v)}
              options={acOptions}
              placeholder="-- Select AC type --"
            />
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Filter Size</label>
              <input
                type="text"
                value={formValues.filterSize}
                onChange={e => updateField('filterSize', e.target.value)}
                placeholder="e.g. 16x25x1"
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>
            <div className="mb-0">
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>HVAC Notes</label>
              <textarea
                value={formValues.hvacNotes}
                onChange={e => updateField('hvacNotes', e.target.value)}
                placeholder="Any HVAC-specific notes..."
                rows={2}
                className="w-full px-4 py-3 rounded-xl outline-none resize-none"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>
          </Card>

          {/* Locks Section */}
          <Card className="mb-4">
            <LockFields values={formValues} onChange={updateField} />
          </Card>

          {/* Change Notes + Photo */}
          <Card className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Change Notes <span style={{ color: colors.textMuted }}>(optional)</span>
            </label>
            <textarea
              value={changeNotes}
              onChange={e => setChangeNotes(e.target.value)}
              placeholder="Why are you making this change?"
              rows={2}
              className="w-full px-4 py-3 rounded-xl outline-none resize-none mb-4"
              style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            />

            <p className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
              Attach Photo <span style={{ color: colors.textMuted }}>(optional)</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoSelect}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer"
              style={{ borderColor: photoFile ? colors.green : colors.border, background: colors.inputBg }}
            >
              <Upload size={28} style={{ color: photoFile ? colors.green : colors.textMuted }} className="mx-auto mb-2" />
              <p className="text-sm" style={{ color: photoFile ? colors.green : colors.textSecondary }}>
                {photoFile ? photoFile.name : 'Tap to upload photo'}
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>JPG/PNG/WEBP up to 8MB</p>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 mb-8">
            <Button
              variant="secondary"
              onClick={() => initForm(selected.details)}
              className="flex-1"
            >
              Reset
            </Button>
            <button
              onClick={() => setShowPreConfirm(true)}
              disabled={uploading || pendingChanges.length === 0}
              className="flex-1 py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: colors.yellow, color: colors.black }}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Detail View ───────────────────────────────────────────────

  if (selected) {
    const d = selected.details || {};

    // Change history for this unit from unitDetailChanges
    const changeHistory = unitDetailChanges
      .filter(c => String(c.unitId) === String(selected.unitId))
      .sort((a, b) => {
        const dateA = a.createdAt?.seconds || (typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() / 1000 : 0);
        const dateB = b.createdAt?.seconds || (typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() / 1000 : 0);
        return dateB - dateA;
      })
      .slice(0, 10);

    const unitWOs = workOrders.filter(wo =>
      wo.unitId === selected.unitId || wo.unit === selected.id
    ).slice(0, 5);

    const hasHvac = d.heatType || d.acType || d.filterSize || d.hvacNotes;
    const hasLocks = d.unitLockCode || d.commonLockCode || d.tenantLockbox;

    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header
          title={selected.name || `Unit ${selected.id}`}
          subtitle={selected.property}
          showBack
          onBack={() => setSelected(null)}
        />
        <div className="px-4">
          {/* HVAC & Filters Card */}
          <Card className="mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Thermometer size={18} style={{ color: colors.yellow }} />
              <span className="font-semibold" style={{ color: colors.textPrimary }}>HVAC & Filters</span>
            </div>
            {hasHvac ? (
              <>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Heat Type</p>
                    <p className="font-medium" style={{ color: colors.textPrimary }}>{d.heatType || '---'}</p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.textMuted }}>AC Type</p>
                    <p className="font-medium" style={{ color: colors.textPrimary }}>{d.acType || '---'}</p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Filter Size</p>
                  <p className="font-medium" style={{ color: colors.textPrimary }}>{d.filterSize || '---'}</p>
                </div>
                {d.hvacNotes && (
                  <div className="rounded-xl p-3" style={{ background: colors.inputBg }}>
                    <p className="text-xs mb-1" style={{ color: colors.textMuted }}>HVAC Notes</p>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>{d.hvacNotes}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: colors.textMuted }}>No HVAC data yet</p>
            )}
          </Card>

          {/* Lock Codes Card */}
          <Card className="mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Lock size={18} style={{ color: colors.yellow }} />
              <span className="font-semibold" style={{ color: colors.textPrimary }}>Lock Codes</span>
            </div>
            {hasLocks ? (
              <>
                {/* Unit Lock */}
                <div className="mb-4">
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Unit Lock</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-bold font-mono" style={{ color: colors.yellow }}>
                      {d.unitLockCode || '---'}
                    </span>
                    {d.unitLockType && (
                      <span className="text-sm px-2 py-1 rounded" style={{ background: colors.inputBg, color: colors.textSecondary }}>
                        {d.unitLockType}
                      </span>
                    )}
                    {d.unitLockCount && (
                      <span className="text-sm" style={{ color: colors.textMuted }}>
                        x{d.unitLockCount}
                      </span>
                    )}
                  </div>
                  {d.unitLockNotes && (
                    <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>{d.unitLockNotes}</p>
                  )}
                </div>

                {/* Common Lock */}
                <div className="mb-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Common Lock</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-bold font-mono" style={{ color: colors.yellow }}>
                      {d.commonLockCode || '---'}
                    </span>
                    {d.commonLockType && (
                      <span className="text-sm px-2 py-1 rounded" style={{ background: colors.inputBg, color: colors.textSecondary }}>
                        {d.commonLockType}
                      </span>
                    )}
                    {d.commonLockCount && (
                      <span className="text-sm" style={{ color: colors.textMuted }}>
                        x{d.commonLockCount}
                      </span>
                    )}
                  </div>
                  {d.commonLockNotes && (
                    <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>{d.commonLockNotes}</p>
                  )}
                </div>

                {/* Tenant Lockbox */}
                {d.tenantLockbox && (
                  <div className="pt-4 border-t" style={{ borderColor: colors.border }}>
                    <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Tenant Lockbox</p>
                    <p className="font-mono font-bold text-lg" style={{ color: colors.yellow }}>{d.tenantLockbox}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: colors.textMuted }}>No lock data yet</p>
            )}
          </Card>

          {/* Unit Info */}
          <Card className="mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Home size={18} style={{ color: colors.yellow }} />
              <span className="font-semibold" style={{ color: colors.textPrimary }}>Unit Information</span>
            </div>
            <div className="mb-4">
              <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Address</p>
              <p className="text-sm" style={{ color: colors.textPrimary }}>{selected.address || selected.name || '---'}</p>
            </div>
            {(selected.bedrooms || selected.bathrooms || selected.sqft) && (
              <div className="flex gap-4 mb-4">
                {selected.bedrooms && (
                  <div>
                    <p className="text-xs" style={{ color: colors.textMuted }}>Beds</p>
                    <p className="font-semibold" style={{ color: colors.textPrimary }}>{selected.bedrooms}</p>
                  </div>
                )}
                {selected.bathrooms && (
                  <div>
                    <p className="text-xs" style={{ color: colors.textMuted }}>Baths</p>
                    <p className="font-semibold" style={{ color: colors.textPrimary }}>{selected.bathrooms}</p>
                  </div>
                )}
                {selected.sqft && (
                  <div>
                    <p className="text-xs" style={{ color: colors.textMuted }}>Sqft</p>
                    <p className="font-semibold" style={{ color: colors.textPrimary }}>{selected.sqft}</p>
                  </div>
                )}
              </div>
            )}
            {selected.resident && (
              <div className="pt-4 border-t" style={{ borderColor: colors.border }}>
                <div className="flex items-center gap-2 mb-3">
                  <User size={16} style={{ color: colors.textMuted }} />
                  <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>Tenant</span>
                </div>
                <p className="font-medium mb-2" style={{ color: colors.textPrimary }}>{selected.resident}</p>
                {selected.residentPhone && (
                  <a
                    href={`tel:${selected.residentPhone}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ background: colors.inputBg }}
                  >
                    <Phone size={14} style={{ color: colors.green }} />
                    <span className="text-sm" style={{ color: colors.green }}>{selected.residentPhone}</span>
                  </a>
                )}
              </div>
            )}
          </Card>

          {/* Edit Button */}
          <div className="mb-4 rounded-2xl p-4" style={{ background: colors.cardBg, border: `2px solid ${colors.yellow}` }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold" style={{ color: colors.yellow }}>Update unit details?</p>
                <p className="text-sm" style={{ color: colors.textSecondary }}>Edit HVAC, locks, or filter info</p>
              </div>
              <button
                onClick={() => { initForm(d); setShowEditForm(true); }}
                className="py-3 px-5 rounded-xl font-bold flex items-center gap-2"
                style={{ background: colors.yellow, color: colors.black }}
              >
                <ClipboardList size={18} /> Edit
              </button>
            </div>
          </div>

          {/* Associated WO */}
          {selected.woId && (
            <div className="rounded-2xl p-4 mb-4" style={{ background: isActiveWO({ status: selected.woStatus }) ? colors.yellowDim : colors.inputBg }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono font-semibold" style={{ color: isActiveWO({ status: selected.woStatus }) ? colors.yellow : colors.textSecondary }}>
                  {selected.woId}
                </span>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    background: isActiveWO({ status: selected.woStatus }) ? colors.greenDim : colors.inputBg,
                    color: isActiveWO({ status: selected.woStatus }) ? colors.green : colors.textMuted
                  }}
                >
                  {selected.woStatus || 'Unknown'}
                </span>
              </div>
              <p className="text-sm" style={{ color: colors.textSecondary }}>{selected.woTask}</p>
            </div>
          )}

          {/* Recent WOs */}
          {unitWOs.length > 1 && (
            <Card className="mb-4">
              <p className="text-sm font-semibold mb-3" style={{ color: colors.textSecondary }}>
                Recent Work Orders ({unitWOs.length})
              </p>
              <div className="space-y-2">
                {unitWOs.slice(0, 3).map(wo => (
                  <div key={wo.id} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: colors.border }}>
                    <div>
                      <span className="font-mono text-sm" style={{ color: colors.yellow }}>{wo.id}</span>
                      <p className="text-xs" style={{ color: colors.textMuted }}>{wo.description?.slice(0, 40)}...</p>
                    </div>
                    <span
                      className="text-xs px-2 py-1 rounded"
                      style={{
                        background: isActiveWO(wo) ? colors.greenDim : colors.inputBg,
                        color: isActiveWO(wo) ? colors.green : colors.textMuted
                      }}
                    >
                      {wo.status}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Change History */}
          <Card className="mb-24">
            <div className="flex items-center gap-2 mb-4">
              <History size={18} style={{ color: colors.yellow }} />
              <span className="font-semibold" style={{ color: colors.textPrimary }}>Change History ({changeHistory.length})</span>
            </div>
            <div className="space-y-4">
              {changeHistory.length > 0 ? (
                changeHistory.map((entry, i) => {
                  const ts = entry.createdAt?.toDate?.()
                    || (entry.createdAt?.seconds ? new Date(entry.createdAt.seconds * 1000) : null)
                    || (typeof entry.createdAt === 'string' ? new Date(entry.createdAt) : null);
                  const dateStr = ts instanceof Date && !isNaN(ts)
                    ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '---';
                  const timeStr = ts instanceof Date && !isNaN(ts)
                    ? ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    : '';

                  return (
                    <div key={entry.id || i} className="flex items-start gap-3 pb-4 border-b last:border-0 last:pb-0" style={{ borderColor: colors.border }}>
                      <div className="w-2 h-2 rounded-full mt-2" style={{ background: i === 0 ? colors.yellow : colors.textMuted }} />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs" style={{ color: colors.textMuted }}>{dateStr} {timeStr && `at ${timeStr}`}</span>
                        </div>
                        <div className="space-y-1 mb-1">
                          {(entry.changes || []).map((c, j) => (
                            <div key={j} className="text-sm">
                              <span style={{ color: colors.textMuted }}>{FIELD_LABELS[c.field] || c.field}: </span>
                              <span className="font-mono" style={{ color: colors.textMuted }}>{c.from || '(empty)'}</span>
                              <span className="mx-1" style={{ color: colors.textMuted }}>&rarr;</span>
                              <span className="font-mono font-bold" style={{ color: colors.yellow }}>{c.to || '(empty)'}</span>
                            </div>
                          ))}
                        </div>
                        {entry.notes && (
                          <p className="text-sm" style={{ color: colors.textSecondary }}>"{entry.notes}"</p>
                        )}
                        <p className="text-xs" style={{ color: colors.textMuted }}>
                          by {entry.changedByName || 'Unknown'}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-center py-4" style={{ color: colors.textMuted }}>No changes recorded yet</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ── List View ─────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
      <Header title="Unit Details" subtitle="Search units & manage details" />
      <div className="px-4">
        {/* Search */}
        <div className="relative mb-4">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
          <input
            type="text"
            placeholder="Search address, unit, property, WO#..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl outline-none"
            style={{ background: colors.cardBg, color: colors.textPrimary }}
          />
        </div>

        {/* View Toggle */}
        <div className="mb-4">
          <PillToggle
            options={[
              { value: 'open', label: `My Open WOs (${openWOUnits.length})` },
              { value: 'all', label: `All Units (${allUnits.length})` }
            ]}
            value={viewMode}
            onChange={setViewMode}
          />
        </div>

        {/* Results */}
        <p className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
          {search ? `Search Results (${filtered.length})` : viewMode === 'open' ? 'Quick Select from Open WOs' : 'All Units'}
        </p>

        <div className="space-y-2">
          {filtered.slice(0, 50).map((unit, idx) => {
            // Show every lock the unit has (unit + common), not just the unit lock.
            const lockCode = formatLockCodes(unit.details);
            const hvac = hvacSummary(unit.details);

            return (
              <button
                key={`${unit.id}-${unit.property}-${idx}`}
                onClick={() => setSelected(unit)}
                className="w-full rounded-2xl p-4 text-left"
                style={{ background: colors.cardBg }}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate" style={{ color: colors.textPrimary }}>
                      {unit.name || `Unit ${unit.id}`}
                    </p>
                    <p className="text-sm truncate" style={{ color: colors.textMuted }}>{unit.property}</p>
                  </div>
                  {lockCode && (
                    <div className="flex items-center gap-1 ml-2">
                      <Lock size={14} style={{ color: colors.textMuted }} />
                      <span className="font-mono font-bold text-lg" style={{ color: colors.yellow }}>{lockCode}</span>
                    </div>
                  )}
                </div>

                {/* HVAC pill */}
                {hvac && (
                  <p className="text-xs mb-2" style={{ color: colors.textMuted }}>{hvac}</p>
                )}

                {/* WO info */}
                <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: colors.border }}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {unit.woId ? (
                      <>
                        <span
                          className="font-mono text-xs px-2 py-1 rounded shrink-0"
                          style={{
                            background: isActiveWO({ status: unit.woStatus }) ? colors.yellowDim : colors.inputBg,
                            color: isActiveWO({ status: unit.woStatus }) ? colors.yellow : colors.textMuted
                          }}
                        >
                          {unit.woId}
                        </span>
                        <span className="text-xs truncate" style={{ color: colors.textMuted }}>{unit.woTask}</span>
                      </>
                    ) : (
                      <span className="text-xs" style={{ color: colors.textMuted }}>No active WO</span>
                    )}
                  </div>
                  <ChevronRight size={16} style={{ color: colors.textMuted }} className="shrink-0 ml-2" />
                </div>
              </button>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-8">
              <ClipboardList size={48} className="mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
              <p style={{ color: colors.textMuted }}>
                {search ? 'No units match your search' : 'No units found'}
              </p>
            </div>
          )}

          {filtered.length > 50 && (
            <p className="text-center text-sm py-4" style={{ color: colors.textMuted }}>
              Showing first 50 results. Refine your search to see more.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocksScreen;
